import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowUpRight, Clock } from "lucide-react";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";
import { API } from "@/data/content";

export default function InsightsSection() {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/articles`)
      .then(({ data }) => setArticles((data.articles || []).slice(0, 3)))
      .catch(() => {});
  }, []);

  if (!articles.length) return null;

  return (
    <section
      id="insights"
      data-testid="insights-section"
      className="mx-auto max-w-7xl px-4 py-24 sm:px-6"
    >
      <Reveal>
        <div className="mb-12 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Eyebrow>Research Library</Eyebrow>
            <SectionTitle className="mt-3">
              Latest <span className="text-brand">Insights</span>
            </SectionTitle>
          </div>
          <Link
            data-testid="insights-view-all-link"
            to="/insights"
            className="group inline-flex w-fit items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-mute transition-colors hover:text-brand"
          >
            Explore all insights
            <ArrowRight
              size={14}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </Link>
        </div>
      </Reveal>

      <div className="grid gap-4 md:grid-cols-3">
        {articles.map((a, i) => (
          <Reveal key={a.slug} delay={i * 0.08}>
            <Link
              data-testid={`insight-card-${a.slug}`}
              to={`/insights/${a.slug}`}
              className="card-trace group flex h-full flex-col overflow-hidden rounded-xl border border-line bg-panel"
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={a.image}
                  alt={a.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 rounded-full bg-brand px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-white">
                  {a.category}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-5">
                <h3 className="flex items-start justify-between gap-2 font-display text-xl font-bold uppercase leading-tight tracking-tight text-ink">
                  {a.title}
                  <ArrowUpRight
                    size={16}
                    className="mt-1 shrink-0 text-dim transition-colors group-hover:text-brand"
                  />
                </h3>
                <p className="mt-2.5 flex-1 text-xs leading-relaxed text-mute sm:text-sm">
                  {a.excerpt}
                </p>
                <p className="mt-4 flex items-center gap-3 border-t border-line pt-3.5 font-mono text-[10px] uppercase tracking-[0.15em] text-dim">
                  <span>{new Date(a.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                  <span className="inline-flex items-center gap-1">
                    <Clock size={10} /> {a.read_minutes} min read
                  </span>
                </p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
